import type { ReactNode } from "react";
import { Card, Flex, Input, Select, Typography } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import type { IntegrationSyncStatus } from "apiClients/base-generated";
import type {
  IntegrationProductsActivityFilter,
  IntegrationProductsLockFilter,
  IntegrationProductsVariantFilter,
} from "../hooks/useIntegrationProductsQuery";
import { IntegrationSyncStatusCode } from "./IntegrationTabs";

const { Text } = Typography;

function filterField(label: string, control: ReactNode) {
  return (
    <Flex vertical gap={6} style={{ minWidth: 200, flex: "0 1 280px" }}>
      <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.2 }}>
        {label}
      </Text>
      {control}
    </Flex>
  );
}

const lockFilterOptions: { label: string; value: IntegrationProductsLockFilter }[] = [
  { label: "Alla", value: "all" },
  { label: "Låsta", value: "locked" },
  { label: "Inte låsta", value: "unlocked" },
];

const activityFilterOptions: { label: string; value: IntegrationProductsActivityFilter }[] = [
  { label: "Alla", value: "all" },
  { label: "Synliga", value: "active" },
  { label: "Dolda", value: "inactive" },
];

const variantFilterOptions: { label: string; value: IntegrationProductsVariantFilter }[] = [
  { label: "Alla", value: "all" },
  { label: "Ja", value: "variant" },
  { label: "Nej", value: "notVariant" },
];

export type NorthmillGroupFilterOption = { value: string; label: string };

export type IntegrationProductsFiltersCardProps = {
  activeSyncStatus: IntegrationSyncStatus;
  search: string;
  onSearchChange: (value: string) => void;
  variantFilter: IntegrationProductsVariantFilter;
  onVariantFilterChange: (value: IntegrationProductsVariantFilter) => void;
  lockFilter: IntegrationProductsLockFilter;
  onLockFilterChange: (value: IntegrationProductsLockFilter) => void;
  activityFilter: IntegrationProductsActivityFilter;
  onActivityFilterChange: (value: IntegrationProductsActivityFilter) => void;
  /** When set (e.g. Northmill page), shows group filter backed by `ListProductItemIntegrationSyncsQuery.northmillGroupId`. */
  northmillGroupId?: string;
  onNorthmillGroupIdChange?: (value: string) => void;
  northmillGroupOptions?: NorthmillGroupFilterOption[];
  northmillGroupsLoading?: boolean;
};

export const IntegrationProductsFiltersCard: React.FC<IntegrationProductsFiltersCardProps> = ({
  activeSyncStatus,
  search,
  onSearchChange,
  variantFilter,
  onVariantFilterChange,
  lockFilter,
  onLockFilterChange,
  activityFilter,
  onActivityFilterChange,
  northmillGroupId = "",
  onNorthmillGroupIdChange,
  northmillGroupOptions,
  northmillGroupsLoading = false,
}) => {
  const showLockActivityFilters = activeSyncStatus !== IntegrationSyncStatusCode.NotSynced;
  const showNorthmillGroupFilter = northmillGroupOptions != null && onNorthmillGroupIdChange != null;

  return (
    <Card
      bordered={false}
      className="criclebox"
      bodyStyle={{ paddingTop: 16, paddingBottom: 16 }}
    >
      <Flex align="flex-end" gap={16} wrap="wrap">
        {filterField(
          "Sök",
          <Input
            allowClear
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Sök produkter eller Art.nr..."
            prefix={<SearchOutlined style={{ color: "rgba(0,0,0,0.25)" }} />}
          />,
        )}
        {filterField(
          "Variantprodukt",
          <Select
            value={variantFilter}
            onChange={onVariantFilterChange}
            options={variantFilterOptions}
            style={{ width: "100%" }}
          />,
        )}
        {showNorthmillGroupFilter
          ? filterField(
              "Grupp (Northmill)",
              <Select
                allowClear
                showSearch
                placeholder="Alla grupper"
                loading={northmillGroupsLoading}
                optionFilterProp="label"
                value={northmillGroupId || undefined}
                onChange={(v) => onNorthmillGroupIdChange(v ?? "")}
                options={northmillGroupOptions}
                style={{ width: "100%" }}
              />,
            )
          : null}
        {/* {filterField(
          "Category",
          <Select defaultValue="all" options={selectAllOption} style={{ width: "100%" }} />,
        )}
        {filterField(
          "Status",
          <Select defaultValue="all" options={selectAllOption} style={{ width: "100%" }} />,
        )} */}
        {showLockActivityFilters && (
          <>
            {filterField(
              "Lås",
              <Select
                value={lockFilter}
                onChange={onLockFilterChange}
                options={lockFilterOptions}
                style={{ width: "100%" }}
              />,
            )}
            {filterField(
              "Synlighet (Northmill)",
              <Select
                value={activityFilter}
                onChange={onActivityFilterChange}
                options={activityFilterOptions}
                style={{ width: "100%" }}
              />,
            )}
          </>
        )}
      </Flex>
    </Card>
  );
};
